import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Code, Text } from "src/components/base/text";
import { Input } from "src/components/base/input";
import { PrimaryButton } from "src/components/base/button";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import { ApiUser } from "src/api/api_user";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify/unstyled";
import { genTotpSecret, genTotpUrl } from "src/utils/totp";
import { ApiPublic } from "src/api/api_public";

/**
 * The properties for {@link AddNewTotpKeyDialog}
 */
export type AddNewTotpKeyDialogProps = {
    /** Callback for submitting the form */
    onSubmit: () => void;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** The users email address */
    mail: string;
    /** The endpoint to use */
    endpoint: "public" | "user";
    /** Whether the dialog is open or not */
    isOpen: boolean;
};

/**
 * Dialog for adding a new totp key
 */
export default function AddNewTotpKeyDialog(props: AddNewTotpKeyDialogProps) {
    const [t] = useTranslation("authentication-dialog");
    const [tg] = useTranslation();

    const [secret] = React.useState(() => genTotpSecret());

    const form = useForm({
        defaultValues: {
            label: "",
            token: "",
        },
        validators: {
            onSubmitAsync: async ({ value: { label, token } }) => {
                const res =
                    props.endpoint === "public"
                        ? await ApiPublic.credentials.createTotpKey({
                              secret: secret,
                              token: token,
                              label: label,
                          })
                        : await ApiUser.credentials.createTotp({ secret, token, label });

                if (res.result === "Err") {
                    return {
                        fields: {
                            token: res.error.token ? t("error.token-invalid") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            toast.success(t("toast.success-new-totp"));
            props.onSubmit();
        },
    });

    const url = genTotpUrl(props.mail, secret);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.endpoint, props.mail]);

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <DialogTitle>{t("heading.create-totp-key")}</DialogTitle>
            <DialogDescription>{t("text.totp-key")}</DialogDescription>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <div className={"flex w-full justify-center"}>
                                <a href={url}>
                                    <QRCodeSVG className={"size-60 rounded-lg bg-white p-2"} value={url} />
                                </a>
                            </div>
                            <span className={"text-center"}>
                                <Text>{tg("description.add-totp-secret")}</Text>
                                <Code>{secret}</Code>
                            </span>
                            <form.Field name={"label"}>
                                {(fieldApi) => (
                                    <Field className={"mt-4"}>
                                        <Label>{t("label.label")}</Label>
                                        <Text className={"my-1"}>{t("description.label-totp")}</Text>
                                        <Input
                                            required={true}
                                            type={"text"}
                                            autoFocus={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.form.state.errors.length !== 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"token"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.totp-token")}</Label>
                                        <Text>{t("description.totp-token")}</Text>
                                        <Input
                                            className={"mt-1"}
                                            autoComplete={"one-time-code"}
                                            required={true}
                                            minLength={6}
                                            maxLength={8}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.form.state.errors.length !== 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <PrimaryButton type={"submit"}>{tg("button.add-totp")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
